/**
 * Advanced Cache Manager for API Performance Optimization
 * 
 * Features:
 * - Multi-tier caching (memory, localStorage, sessionStorage)
 * - Request deduplication
 * - Cache invalidation strategies
 * - Performance monitoring
 * - LRU eviction policy
 */

export interface CacheConfig {
  enabled: boolean;
  defaultTTL: number; // Time to live in milliseconds
  maxMemoryEntries: number;
  maxStorageSize: number; // In bytes
  storage: 'memory' | 'localStorage' | 'sessionStorage' | 'hybrid';
  compressionEnabled: boolean;
  metrics: boolean;
}

export interface CacheEntry<T = unknown> {
  data: T;
  timestamp: number;
  ttl: number;
  accessCount: number;
  lastAccessed: number;
  size: number;
  compressed?: boolean;
  etag?: string;
  version?: string;
}

export interface CacheMetrics {
  hits: number;
  misses: number;
  sets: number;
  deletes: number;
  evictions: number;
  hitRate: number;
  avgResponseTime: number;
  totalSize: number;
  entryCount: number;
}

export interface RequestKey {
  url: string;
  method: string;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  establishmentId?: string;
}

const DEFAULT_CONFIG: CacheConfig = {
  enabled: true,
  defaultTTL: 5 * 60 * 1000, // 5 minutes
  maxMemoryEntries: 1000,
  maxStorageSize: 10 * 1024 * 1024, // 10MB
  storage: 'hybrid',
  compressionEnabled: true,
  metrics: true,
};

export class CacheManager {
  private config: CacheConfig;
  private memoryCache = new Map<string, CacheEntry>();
  private accessOrder = new Map<string, number>(); // For LRU implementation
  private metrics: CacheMetrics;
  private pendingRequests = new Map<string, Promise<unknown>>(); // Request deduplication
  private invalidationRules = new Map<string, RegExp[]>();
  private compressionWorker?: Worker;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.metrics = this.initializeMetrics();
    
    if (this.config.compressionEnabled) {
      this.initializeCompression();
    }

    // Set up periodic cleanup
    setInterval(() => this.cleanup(), 60 * 1000); // Every minute
    
    // Set up metrics reporting
    if (this.config.metrics) {
      setInterval(() => this.reportMetrics(), 5 * 60 * 1000); // Every 5 minutes
    }
  }

  /**
   * Get cached data for a request
   */
  async get<T = unknown>(key: string | RequestKey): Promise<T | null> {
    const cacheKey = this.generateKey(key);
    const startTime = Date.now();

    try {
      // Check memory cache first
      const memoryEntry = this.getFromMemory<T>(cacheKey);
      if (memoryEntry) {
        this.updateAccessTime(cacheKey);
        this.recordHit(Date.now() - startTime);
        return memoryEntry;
      }

      // Check persistent storage
      if (this.config.storage === 'hybrid' || this.config.storage !== 'memory') {
        const storageEntry = await this.getFromStorage<T>(cacheKey);
        if (storageEntry) {
          // Promote to memory cache
          this.setInMemory(cacheKey, storageEntry);
          this.recordHit(Date.now() - startTime);
          return storageEntry.data;
        }
      }

      this.recordMiss();
      return null;
    } catch (error) {
      console.warn('Cache get error:', error);
      this.recordMiss();
      return null;
    }
  }

  /**
   * Set cached data
   */
  async set<T = unknown>(
    key: string | RequestKey, 
    data: T, 
    options: {
      ttl?: number;
      tags?: string[];
      etag?: string;
      version?: string;
    } = {}
  ): Promise<void> {
    const cacheKey = this.generateKey(key);
    const ttl = options.ttl || this.config.defaultTTL;
    const size = this.calculateSize(data);

    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      accessCount: 1,
      lastAccessed: Date.now(),
      size,
      etag: options.etag,
      version: options.version,
    };

    try {
      // Compress if enabled and data is large enough
      if (this.config.compressionEnabled && size > 1024) { // 1KB threshold
        entry.compressed = true;
        entry.data = await this.compress(data) as T;
      }

      // Set in memory cache
      this.setInMemory(cacheKey, entry);

      // Set in persistent storage if configured
      if (this.config.storage === 'hybrid' || this.config.storage !== 'memory') {
        await this.setInStorage(cacheKey, entry);
      }

      // Set up invalidation rules if tags provided
      if (options.tags) {
        this.addInvalidationRules(options.tags, cacheKey);
      }

      this.metrics.sets++;
    } catch (error) {
      console.warn('Cache set error:', error);
    }
  }

  /**
   * Delete cached entry
   */
  async delete(key: string | RequestKey): Promise<void> {
    const cacheKey = this.generateKey(key);

    try {
      this.memoryCache.delete(cacheKey);
      this.accessOrder.delete(cacheKey);
      
      if (this.config.storage !== 'memory') {
        await this.deleteFromStorage(cacheKey);
      }

      this.metrics.deletes++;
    } catch (error) {
      console.warn('Cache delete error:', error);
    }
  }

  /**
   * Invalidate cache entries by pattern or tags
   */
  async invalidate(pattern: string | RegExp | string[]): Promise<void> {
    try {
      if (Array.isArray(pattern)) {
        // Invalidate by tags
        for (const tag of pattern) {
          const rules = this.invalidationRules.get(tag);
          if (rules) {
            for (const rule of rules) {
              await this.invalidateByPattern(rule);
            }
          }
        }
      } else if (pattern instanceof RegExp) {
        await this.invalidateByPattern(pattern);
      } else {
        // Convert string to regex
        const regex = new RegExp(pattern.replace(/\*/g, '.*'));
        await this.invalidateByPattern(regex);
      }
    } catch (error) {
      console.warn('Cache invalidation error:', error);
    }
  }

  /**
   * Request deduplication wrapper
   */
  async deduplicate<T>(
    key: string | RequestKey,
    requestFn: () => Promise<T>,
    options: {
      ttl?: number;
      tags?: string[];
      forceRefresh?: boolean;
    } = {}
  ): Promise<T> {
    const cacheKey = this.generateKey(key);

    // Check if request is already pending
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest && !options.forceRefresh) {
      return pendingRequest as Promise<T>;
    }

    // Check cache first
    if (!options.forceRefresh) {
      const cached = await this.get<T>(cacheKey);
      if (cached !== null) {
        return cached;
      }
    }

    // Execute request with deduplication
    const requestPromise = requestFn()
      .then(async (result) => {
        // Cache the result
        await this.set(cacheKey, result, options);
        this.pendingRequests.delete(cacheKey);
        return result;
      })
      .catch((error) => {
        this.pendingRequests.delete(cacheKey);
        throw error;
      });

    this.pendingRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }

  /**
   * Batch operations for multiple keys
   */
  async getMany<T = unknown>(keys: (string | RequestKey)[]): Promise<Map<string, T | null>> {
    const results = new Map<string, T | null>();
    
    await Promise.all(
      keys.map(async (key) => {
        const cacheKey = this.generateKey(key);
        const value = await this.get<T>(cacheKey);
        results.set(cacheKey, value);
      })
    );

    return results;
  }

  /**
   * Get cache statistics
   */
  getMetrics(): CacheMetrics {
    const totalRequests = this.metrics.hits + this.metrics.misses;
    
    return {
      ...this.metrics,
      hitRate: totalRequests > 0 ? this.metrics.hits / totalRequests : 0,
      totalSize: this.calculateTotalSize(),
      entryCount: this.memoryCache.size,
    };
  }

  /**
   * Clear all cache
   */
  async clear(): Promise<void> {
    try {
      this.memoryCache.clear();
      this.accessOrder.clear();
      this.pendingRequests.clear();
      this.invalidationRules.clear();

      if (this.config.storage !== 'memory') {
        await this.clearStorage();
      }

      this.metrics = this.initializeMetrics();
    } catch (error) {
      console.warn('Cache clear error:', error);
    }
  }

  /**
   * Configure cache settings
   */
  configure(newConfig: Partial<CacheConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  // Private methods

  public generateKey(key: string | RequestKey): string {
    if (typeof key === 'string') {
      return key;
    }

    const { url, method, params, headers, establishmentId } = key;
    const normalizedParams = params ? JSON.stringify(params, Object.keys(params).sort()) : '';
    const normalizedHeaders = headers ? JSON.stringify(headers, Object.keys(headers).sort()) : '';
    const establishment = establishmentId || '';
    
    return `${method}:${url}:${normalizedParams}:${normalizedHeaders}:${establishment}`;
  }

  private getFromMemory<T>(key: string): T | null {
    const entry = this.memoryCache.get(key);
    if (!entry) return null;

    // Check if expired
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.memoryCache.delete(key);
      this.accessOrder.delete(key);
      this.metrics.evictions++;
      return null;
    }

    return (entry.compressed ? this.decompress(entry.data) : entry.data) as T;
  }

  private setInMemory<T>(key: string, entry: CacheEntry<T>): void {
    // Check memory limits
    if (this.memoryCache.size >= this.config.maxMemoryEntries) {
      this.evictLRU();
    }

    this.memoryCache.set(key, entry);
    this.accessOrder.set(key, Date.now());
  }

  private async getFromStorage<T>(key: string): Promise<CacheEntry<T> | null> {
    try {
      const storage = this.getStorage();
      const item = storage.getItem(`cache:${key}`);
      
      if (!item) return null;

      const entry: CacheEntry<T> = JSON.parse(item);
      
      // Check if expired
      if (Date.now() - entry.timestamp > entry.ttl) {
        storage.removeItem(`cache:${key}`);
        return null;
      }

      return entry;
    } catch (error) {
      console.warn('Storage get error:', error);
      return null;
    }
  }

  private async setInStorage<T>(key: string, entry: CacheEntry<T>): Promise<void> {
    try {
      const storage = this.getStorage();
      const serialized = JSON.stringify(entry);
      
      // Check storage size limits
      if (serialized.length > this.config.maxStorageSize) {
        return; // Skip storing large entries
      }

      storage.setItem(`cache:${key}`, serialized);
    } catch (error) {
      const err = error as Error & { name?: string };
      if (err.name === 'QuotaExceededError') {
        // Clear some storage space
        await this.clearOldestStorageEntries();
        // Try again
        try {
          const storage = this.getStorage();
          storage.setItem(`cache:${key}`, JSON.stringify(entry));
        } catch (retryError) {
          console.warn('Storage set retry failed:', retryError);
        }
      } else {
        console.warn('Storage set error:', error);
      }
    }
  }

  private async deleteFromStorage(key: string): Promise<void> {
    try {
      const storage = this.getStorage();
      storage.removeItem(`cache:${key}`);
    } catch (error) {
      console.warn('Storage delete error:', error);
    }
  }

  private getStorage(): Storage {
    switch (this.config.storage) {
      case 'localStorage':
        return localStorage;
      case 'sessionStorage':
        return sessionStorage;
      case 'hybrid':
      default:
        return localStorage;
    }
  }

  private updateAccessTime(key: string): void {
    const entry = this.memoryCache.get(key);
    if (entry) {
      entry.accessCount++;
      entry.lastAccessed = Date.now();
      this.accessOrder.set(key, Date.now());
    }
  }

  private evictLRU(): void {
    let oldestKey: string | null = null;
    let oldestTime = Date.now();

    this.accessOrder.forEach((time, key) => {
      if (time < oldestTime) {
        oldestTime = time;
        oldestKey = key;
      }
    });

    if (oldestKey) {
      this.memoryCache.delete(oldestKey);
      this.accessOrder.delete(oldestKey);
      this.metrics.evictions++;
    }
  }

  private async invalidateByPattern(pattern: RegExp): Promise<void> {
    const keysToDelete: string[] = [];

    // Check memory cache
    this.memoryCache.forEach((_, key) => {
      if (pattern.test(key)) {
        keysToDelete.push(key);
      }
    });

    // Check storage
    if (this.config.storage !== 'memory') {
      const storage = this.getStorage();
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith('cache:') && pattern.test(key.substring(6))) {
          keysToDelete.push(key.substring(6));
        }
      }
    }

    // Delete all matching keys
    await Promise.all(keysToDelete.map(key => this.delete(key)));
  }

  private addInvalidationRules(tags: string[], key: string): void {
    tags.forEach(tag => {
      if (!this.invalidationRules.has(tag)) {
        this.invalidationRules.set(tag, []);
      }
      const regex = new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      this.invalidationRules.get(tag)!.push(regex);
    });
  }

  private calculateSize(data: unknown): number {
    return JSON.stringify(data).length * 2; // Rough estimate (UTF-16)
  }

  private calculateTotalSize(): number {
    let total = 0;
    this.memoryCache.forEach(entry => {
      total += entry.size;
    });
    return total;
  }

  private async compress<T>(data: T): Promise<T | string> {
    // Implement compression if worker is available
    if (this.compressionWorker) {
      return new Promise((resolve, reject) => {
        this.compressionWorker!.postMessage({ action: 'compress', data });
        this.compressionWorker!.onmessage = (e) => {
          if (e.data.error) {
            reject(new Error(e.data.error));
          } else {
            resolve(e.data.result);
          }
        };
      });
    }
    
    // Fallback: simple JSON compression (remove whitespace)
    return JSON.stringify(data);
  }

  private decompress<T>(data: T): T | unknown {
    // Simple decompression fallback
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch {
        return data;
      }
    }
    return data;
  }

  private initializeCompression(): void {
    try {
      // Create compression worker if available
      const workerCode = `
        self.onmessage = function(e) {
          try {
            const { action, data } = e.data;
            if (action === 'compress') {
              // Simple compression: just JSON stringify for now
              const result = JSON.stringify(data);
              self.postMessage({ result });
            } else if (action === 'decompress') {
              const result = JSON.parse(data);
              self.postMessage({ result });
            }
          } catch (error) {
            self.postMessage({ error: error.message });
          }
        };
      `;
      
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      this.compressionWorker = new Worker(URL.createObjectURL(blob));
    } catch (error) {
      console.warn('Compression worker initialization failed:', error);
    }
  }

  private initializeMetrics(): CacheMetrics {
    return {
      hits: 0,
      misses: 0,
      sets: 0,
      deletes: 0,
      evictions: 0,
      hitRate: 0,
      avgResponseTime: 0,
      totalSize: 0,
      entryCount: 0,
    };
  }

  private recordHit(responseTime: number): void {
    this.metrics.hits++;
    this.updateAvgResponseTime(responseTime);
  }

  private recordMiss(): void {
    this.metrics.misses++;
  }

  private updateAvgResponseTime(responseTime: number): void {
    const totalRequests = this.metrics.hits + this.metrics.misses;
    this.metrics.avgResponseTime = 
      (this.metrics.avgResponseTime * (totalRequests - 1) + responseTime) / totalRequests;
  }

  private cleanup(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    // Clean memory cache
    this.memoryCache.forEach((entry, key) => {
      if (now - entry.timestamp > entry.ttl) {
        expiredKeys.push(key);
      }
    });

    expiredKeys.forEach(key => {
      this.memoryCache.delete(key);
      this.accessOrder.delete(key);
      this.metrics.evictions++;
    });
  }

  private async clearOldestStorageEntries(): Promise<void> {
    try {
      const storage = this.getStorage();
      const entries: { key: string; timestamp: number }[] = [];

      // Collect all cache entries with timestamps
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith('cache:')) {
          try {
            const item = storage.getItem(key);
            if (item) {
              const entry = JSON.parse(item);
              entries.push({ key, timestamp: entry.timestamp });
            }
          } catch {
            // Remove corrupted entries
            storage.removeItem(key);
          }
        }
      }

      // Sort by timestamp and remove oldest 25%
      entries.sort((a, b) => a.timestamp - b.timestamp);
      const removeCount = Math.floor(entries.length * 0.25);
      
      for (let i = 0; i < removeCount; i++) {
        const entry = entries[i];
        if (entry) storage.removeItem(entry.key);
      }
    } catch (error) {
      console.warn('Storage cleanup error:', error);
    }
  }

  private async clearStorage(): Promise<void> {
    try {
      const storage = this.getStorage();
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith('cache:')) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => storage.removeItem(key));
    } catch (error) {
      console.warn('Storage clear error:', error);
    }
  }

  private reportMetrics(): void {
    if (this.config.metrics) {
      const metrics = this.getMetrics();
      console.log('Cache Metrics:', metrics);
      
      // Could emit to analytics service here
      // analyticsService.track('cache_metrics', metrics);
    }
  }
}

// Export singleton instance
export const cacheManager = new CacheManager();

// Export cache configurations for different scenarios
export const cacheConfigs = {
  // For frequently accessed, stable data
  longTerm: {
    ttl: 30 * 60 * 1000, // 30 minutes
    tags: ['static', 'reference'],
  },
  
  // For user-specific data
  userSpecific: {
    ttl: 10 * 60 * 1000, // 10 minutes
    tags: ['user', 'profile'],
  },
  
  // For establishment-specific data
  establishmentSpecific: {
    ttl: 15 * 60 * 1000, // 15 minutes
    tags: ['establishment', 'context'],
  },
  
  // For search results and paginated data
  searchResults: {
    ttl: 5 * 60 * 1000, // 5 minutes
    tags: ['search', 'pagination'],
  },
  
  // For real-time data that changes frequently
  realTime: {
    ttl: 1 * 60 * 1000, // 1 minute
    tags: ['realtime', 'notifications'],
  },
};